import { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  getAdminAnalyticsOverview,
  getAdminAnalyticsSignupsByDay,
  getAdminAnalyticsProductHealth,
  getAdminAnalyticsEngagement,
  getAdminAnalyticsSubscriptions,
  getAdminAnalyticsTopContent,
  type AdminAnalyticsOverview,
  type AdminAnalyticsProductHealthPoint,
  type AdminAnalyticsEngagementPoint,
  type AdminAnalyticsSubscriptionsFunnel,
  type AdminAnalyticsTopContentItem,
  type SignupsByDayItem,
} from '@/lib/api/admin'

type Range = '7d' | '30d'

export function AdminAnalytics() {
  const [range, setRange] = useState<Range>('7d')
  const [overview, setOverview] = useState<AdminAnalyticsOverview | null>(null)
  const [signupsData, setSignupsData] = useState<SignupsByDayItem[]>([])
  const [productHealth, setProductHealth] = useState<AdminAnalyticsProductHealthPoint[]>([])
  const [engagement, setEngagement] = useState<AdminAnalyticsEngagementPoint[]>([])
  const [subscriptions, setSubscriptions] = useState<AdminAnalyticsSubscriptionsFunnel | null>(
    null,
  )
  const [topContent, setTopContent] = useState<AdminAnalyticsTopContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        // Always load the original, stable analytics first.
        const [ov, signups] = await Promise.all([
          getAdminAnalyticsOverview(),
          getAdminAnalyticsSignupsByDay('7d'),
        ])
        if (cancelled) return
        setOverview(ov)
        setSignupsData(signups.items ?? [])

        // Fire telemetry-powered sections in the background; treat 404s as \"not yet available\"
        // so we don't break the whole page when backend routes aren't deployed.
        void (async () => {
          try {
            const [health, engagementRes, subs, top] = await Promise.all([
              getAdminAnalyticsProductHealth(range),
              getAdminAnalyticsEngagement(range),
              getAdminAnalyticsSubscriptions(range),
              getAdminAnalyticsTopContent(range, 10),
            ])
            if (cancelled) return
            setProductHealth(health.items ?? [])
            setEngagement(engagementRes.items ?? [])
            setSubscriptions(subs)
            setTopContent(top.items ?? [])
          } catch (e: unknown) {
            // If these endpoints 404 or fail, just leave the new sections empty.
            // eslint-disable-next-line no-console
            console.warn('Telemetry analytics endpoints unavailable; skipping extended sections.', e)
          } finally {
            if (!cancelled) setLoading(false)
          }
        })()
      } catch (err: unknown) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load analytics overview')
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [range])

  const primaryTiles = useMemo(
    () =>
      overview
        ? [
            {
              label: 'Daily active users',
              value: overview.dailyActiveUsers.toLocaleString(),
              helper: 'Unique users with watch activity in last 24 hours',
            },
            {
              label: 'New signups (7 days)',
              value: overview.newSignups7d.toLocaleString(),
              helper: 'New accounts created in the last 7 days',
            },
            {
              label: 'Hours watched (7 days)',
              value: overview.hoursWatched7d.toFixed(1),
              helper: 'Total watch time in the last 7 days',
            },
            {
              label: 'Creator approval rate',
              value: `${overview.creatorApprovalRate}%`,
              helper: 'Approved vs submitted shows',
            },
          ]
        : [],
    [overview],
  )

  const telemetryTiles = useMemo(() => {
    if (!overview) return []
    return [
      {
        label: 'Crash rate (7 days)',
        value:
          typeof overview.crashRate7d === 'number' ? `${overview.crashRate7d}%` : 'Not available',
        helper: 'Sessions with errors (from telemetry when available)',
      },
      {
        label: 'Buffering rate (7 days)',
        value:
          typeof overview.bufferingRate7d === 'number'
            ? `${overview.bufferingRate7d}%`
            : 'Not available',
        helper: 'Sessions with buffering (from telemetry when available)',
      },
      {
        label: 'Active subscriptions',
        value:
          typeof overview.activeSubscriptions === 'number'
            ? overview.activeSubscriptions.toLocaleString()
            : 'Not available',
        helper: 'Subscriptions currently marked active',
      },
      {
        label: 'Trial → paid rate',
        value:
          typeof overview.trialToPaidRate === 'number'
            ? `${overview.trialToPaidRate}%`
            : 'Not available',
        helper: 'Percent of trials that convert to paid',
      },
    ]
  }, [overview])

  const subscriptionsFunnel = useMemo(
    () => subscriptions?.funnel ?? { visits: 0, trialsStarted: 0, activeSubscriptions: 0 },
    [subscriptions],
  )

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="mt-1 text-sm text-gray-400">
            Overview of Binyuma TV performance from live backend data.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 text-xs">
          <span className="text-gray-400">Range:</span>
          <div className="inline-flex rounded-full bg-white/5 p-1">
            {(['7d', '30d'] as Range[]).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRange(value)}
                className={`px-3 py-1 rounded-full ${
                  range === value ? 'bg-[#D4AF37] text-black' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {value === '7d' ? 'Last 7 days' : 'Last 30 days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {loading && !overview
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4"
              >
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">—</p>
                <p className="text-2xl font-semibold text-white mb-1">—</p>
                <p className="text-xs text-gray-400">Loading…</p>
              </div>
            ))
          : primaryTiles.map((tile) => (
              <div
                key={tile.label}
                className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4"
              >
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{tile.label}</p>
                <p className="text-2xl font-semibold text-white mb-1">{tile.value}</p>
                <p className="text-xs text-gray-400">{tile.helper}</p>
              </div>
            ))}
      </div>

      {overview && (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            Telemetry-derived metrics (incremental)
          </p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {telemetryTiles.map((tile) => (
              <div
                key={tile.label}
                className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4"
              >
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{tile.label}</p>
                <p className="text-2xl font-semibold text-white mb-1">{tile.value}</p>
                <p className="text-xs text-gray-400">{tile.helper}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signups timeseries */}
      <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-4">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          New signups by day (last 7 days)
        </p>
        {loading && signupsData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-400">
            Loading chart…
          </div>
        ) : signupsData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-400">
            No signup data for this period.
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={signupsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="day" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#020617',
                    borderRadius: 8,
                    borderColor: '#111827',
                    fontSize: 12,
                  }}
                />
                <Legend />
                <Bar dataKey="viewers" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                <Bar dataKey="creators" fill="#D72638" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Product health section */}
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Product health: crash & buffering rate
          </p>
          {loading && productHealth.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-gray-400">
              Loading health metrics…
            </div>
          ) : productHealth.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-gray-400">
              No health metrics available for this period.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={productHealth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="day" stroke="#6b7280" fontSize={11} />
                  <YAxis stroke="#6b7280" fontSize={11} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#020617',
                      borderRadius: 8,
                      borderColor: '#111827',
                      fontSize: 12,
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="crashRate"
                    name="Crash rate %"
                    stroke="#F97373"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="bufferingRate"
                    name="Buffering rate %"
                    stroke="#FACC15"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Engagement section */}
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Engagement: DAU & hours watched
          </p>
          {loading && engagement.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-gray-400">
              Loading engagement metrics…
            </div>
          ) : engagement.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-gray-400">
              No engagement metrics available for this period.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={engagement}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="day" stroke="#6b7280" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#6b7280" fontSize={11} />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#6b7280"
                    fontSize={11}
                    tickFormatter={(v) => `${v.toFixed ? v.toFixed(1) : v}h`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#020617',
                      borderRadius: 8,
                      borderColor: '#111827',
                      fontSize: 12,
                    }}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="dau"
                    name="DAU"
                    fill="#38BDF8"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="hoursWatched"
                    name="Hours watched"
                    fill="#22C55E"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Business / subscriptions funnel */}
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Subscriptions funnel (approximate)
          </p>
          {loading && !subscriptions ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">
              Loading subscription metrics…
            </div>
          ) : !subscriptions ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">
              No subscription metrics available.
            </div>
          ) : (
            <div className="space-y-3">
              {[
                {
                  label: 'Visitors with subscription activity',
                  value: subscriptionsFunnel.visits,
                },
                {
                  label: 'Trials started',
                  value: subscriptionsFunnel.trialsStarted,
                },
                {
                  label: 'Active subscriptions',
                  value: subscriptionsFunnel.activeSubscriptions,
                },
              ].map((row, idx) => {
                const max = Math.max(
                  subscriptionsFunnel.visits,
                  subscriptionsFunnel.trialsStarted,
                  subscriptionsFunnel.activeSubscriptions,
                  1,
                )
                const width = Math.max(8, Math.round(((row.value || 0) / max) * 100))
                const colors = ['#4ADE80', '#FACC15', '#F97373'] as const
                return (
                  <div key={row.label} className="space-y-1">
                    <div className="flex items-center justify-between text-xs text-gray-300">
                      <span>{row.label}</span>
                      <span className="font-medium">{row.value.toLocaleString()}</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-gray-900 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${width}%`,
                          backgroundColor: colors[idx] ?? '#D4AF37',
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Top content / creators */}
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Top series by views (approximate)
          </p>
          {loading && topContent.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">
              Loading top content…
            </div>
          ) : topContent.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">
              No series data available yet.
            </div>
          ) : (
            <div className="space-y-3">
              {topContent.map((item) => (
                <div
                  key={item.id}
                  className="rounded-lg border border-gray-800 bg-black/40 px-3 py-2 flex items-center justify-between gap-3"
                >
                  <div>
                    <p className="text-sm font-medium text-white">{item.title}</p>
                    <p className="text-xs text-gray-400">{item.creator}</p>
                  </div>
                  <p className="text-xs text-gray-300">
                    {item.totalViews.toLocaleString()} views
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
